import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  Divider,
  Stack,
  Icon,
} from "@mui/material";
import { getProviderDetail } from "../../../api/providers";
import { useCompany } from "../../../contexts/CompanyContext";
import { useSnack } from "../../../services/SnakbarProvider";
import { handleError } from "../../../services/errorHandler";
import ActivityTable from "../ActivityTable";

export default function ProviderVisualization({ props }) {
  const { id } = props;
  const { selectedCompany } = useCompany();
  const { openSnack } = useSnack();
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState(null);

  useEffect(() => {
    const fetchProviderDetails = async () => {
      if (!id || !selectedCompany) return;
      
      try {
        setLoading(true);
        const res = await getProviderDetail(id, selectedCompany.id);
        setProvider(res.data);
      } catch (e) {
        openSnack(handleError(e), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchProviderDetails();
  }, [id, selectedCompany, openSnack]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!provider) {
    return (
      <Box p={4}>
        <Typography variant="h6" color="error">
          No se pudo cargar la información del proveedor
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Provider Information */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Información del Proveedor
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Nombre o Razón Social
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {provider.legalName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Clave
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {provider.code || "N/A"}
                </Typography>
              </Grid>
              {provider.oldCode && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Clave Anterior
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {provider.oldCode}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  RFC / Tax ID
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {provider.taxId || "N/A"}
                </Typography>
              </Grid>
              {provider.providerScope && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Alcance del Proveedor
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {provider.providerScope.name || "N/A"}
                  </Typography>
                </Grid>
              )}
              {provider.legalEntityType && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Tipo de Entidad Legal
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {provider.legalEntityType.name || "N/A"}
                  </Typography>
                </Grid>
              )}
              {provider.regime && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Régimen Fiscal
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {provider.regime.name || "N/A"}
                  </Typography>
                </Grid>
              )}
              {provider.subregime && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Subregimen Fiscal
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {provider.subregime.name || "N/A"}
                  </Typography>
                </Grid>
              )}
              {provider.corporationType && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Tipo de Corporación
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {provider.corporationType.name || "N/A"}
                  </Typography>
                </Grid>
              )}
              {provider.website && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Sitio Web
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {provider.website}
                  </Typography>
                </Grid>
              )}
              {provider.descripcion && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Descripción
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {provider.descripcion}
                  </Typography>
                </Grid>
              )}
              {provider.creditDays !== undefined && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Días de Crédito
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {provider.creditDays}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Bank Accounts */}
        {provider.bankDetails && provider.bankDetails.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Información Bancaria
              </Typography>
              <Divider sx={{ my: 2 }} />
              {provider.bankDetails.map((bankDetail, index) => (
                <Box key={index} mt={index > 0 ? 4 : 0}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        {bankDetail.bank?.name || bankDetail.foreignBank || "N/A"}
                      </Typography>
                    </Grid>
                    {bankDetail.isForeign && (
                      <Grid item xs={12}>
                        <Typography variant="button" color="success.main">
                          CUENTA EXTRANJERA
                        </Typography>
                      </Grid>
                    )}
                    <Grid container item xs={12}>
                      <Stack
                        alignItems="center"
                        direction="row"
                        gap={2}
                        justifyContent="space-around"
                      >
                        <Icon>account_balance</Icon>
                        <Typography variant="button" color="primary.main">
                          Cuenta
                        </Typography>
                        <Typography>{bankDetail.bankAccount || "N/A"}</Typography>
                      </Stack>
                    </Grid>
                    <Grid container item xs={12}>
                      <Stack
                        alignItems="center"
                        direction="row"
                        gap={2}
                        justifyContent="space-around"
                      >
                        <Icon>currency_exchange</Icon>
                        <Typography variant="button" color="primary.main">
                          Moneda
                        </Typography>
                        <Typography variant="button" color="success.main">
                          {bankDetail.currency?.symbol || "-"}
                        </Typography>
                        <Typography>{bankDetail.currency?.name || "N/A"}</Typography>
                      </Stack>
                    </Grid>
                    {bankDetail.isForeign ? (
                      <>
                        {bankDetail.swift && (
                          <Grid container item xs={12}>
                            <Stack
                              alignItems="center"
                              direction="row"
                              gap={2}
                              justifyContent="space-around"
                            >
                              <Icon>swap_horiz</Icon>
                              <Typography variant="button" color="primary.main">
                                SWIFT
                              </Typography>
                              <Typography>{bankDetail.swift}</Typography>
                            </Stack>
                          </Grid>
                        )}
                        {bankDetail.aba && (
                          <Grid container item xs={12}>
                            <Stack
                              alignItems="center"
                              direction="row"
                              gap={2}
                              justifyContent="space-around"
                            >
                              <Icon>baseline_sync_alt</Icon>
                              <Typography variant="button" color="primary.main">
                                ABA
                              </Typography>
                              <Typography>{bankDetail.aba}</Typography>
                            </Stack>
                          </Grid>
                        )}
                        {bankDetail.routingAba && (
                          <Grid container item xs={12}>
                            <Stack
                              alignItems="center"
                              direction="row"
                              gap={2}
                              justifyContent="space-around"
                            >
                              <Icon>outlined_flag</Icon>
                              <Typography variant="button" color="primary.main">
                                Routing ABA
                              </Typography>
                              <Typography>{bankDetail.routingAba}</Typography>
                            </Stack>
                          </Grid>
                        )}
                      </>
                    ) : (
                      <>
                        {bankDetail.clabe && (
                          <Grid container item xs={12}>
                            <Stack
                              alignItems="center"
                              direction="row"
                              gap={2}
                              justifyContent="space-around"
                            >
                              <Icon>vpn_key</Icon>
                              <Typography variant="button" color="primary.main">
                                CLABE
                              </Typography>
                              <Typography>{bankDetail.clabe}</Typography>
                            </Stack>
                          </Grid>
                        )}
                        {bankDetail.cie && (
                          <Grid container item xs={12}>
                            <Stack
                              alignItems="center"
                              direction="row"
                              gap={2}
                              justifyContent="space-evenly"
                            >
                              <Icon>compare_arrows</Icon>
                              <Typography variant="button" color="primary.main">
                                Cuenta CIE
                              </Typography>
                              <Typography>{bankDetail.cie}</Typography>
                            </Stack>
                          </Grid>
                        )}
                        {bankDetail.cieRef && (
                          <Grid container item xs={12}>
                            <Stack
                              alignItems="center"
                              direction="row"
                              gap={2}
                              justifyContent="space-evenly"
                            >
                              <Icon>compare_arrows</Icon>
                              <Typography variant="button" color="primary.main">
                                Referencia CIE
                              </Typography>
                              <Typography>{bankDetail.cieRef}</Typography>
                            </Stack>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </Box>
              ))}
            </Paper>
          </Grid>
        )}

        {/* Contact Information */}
        {provider.contacts && provider.contacts.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom>
                Contactos
              </Typography>
              <Divider sx={{ my: 2 }} />
              {provider.contacts.map((contact, index) => (
                <Box key={index} mt={index > 0 ? 4 : 0}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        {contact.name || "N/A"} {contact.contactType?.name && `| ${contact.contactType.name}`}
                      </Typography>
                    </Grid>
                    {contact.email && (
                      <Grid item xs={12}>
                        <Stack alignItems="center" direction="row" gap={2}>
                          <Icon>email</Icon>
                          <Typography>{contact.email}</Typography>
                        </Stack>
                      </Grid>
                    )}
                    {contact.phone && (
                      <Grid item xs={12}>
                        <Stack alignItems="center" direction="row" gap={2}>
                          <Icon>phone</Icon>
                          <Typography>{contact.phone}</Typography>
                          {contact.ext && (
                            <>
                              <Typography variant="button" color="primary.main">
                                ext.
                              </Typography>
                              <Typography variant="button">{contact.ext}</Typography>
                            </>
                          )}
                        </Stack>
                      </Grid>
                    )}
                    {contact.mobile && (
                      <Grid item xs={12}>
                        <Stack alignItems="center" direction="row" gap={2}>
                          <Icon>smartphone</Icon>
                          <Typography>{contact.mobile}</Typography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              ))}
            </Paper>
          </Grid>
        )}

        {/* Activities */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Actividades Económicas
            </Typography>
            <Divider sx={{ my: 2 }} />
            <ActivityTable props={{ id }} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

